import React, { useEffect, useState } from "react";
import { api, extractError } from "../lib/api";
import { Pill } from "./Ui";
import { formatListDate } from "../lib/purchaseListMeta";
import BackendExportButton from "./BackendExportButton";
import { cn } from "@/lib/utils";

function fmtQtl(v) {
  if (v == null || v === "") return "—";
  return Number(v).toFixed(2);
}

function fmtMoney(v) {
  if (v == null || v === "") return "—";
  return `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
}

function stateTone(state) {
  if (state === "COMPLETED") return "sage";
  if (state === "ACCOUNTS_PENDING") return "gold";
  return "muted";
}

function shortState(state) {
  const raw = String(state || "");
  const map = {
    ACCOUNTS_PENDING: "Accts pending",
    COMPLETED: "Completed",
    REJECTED: "Rejected",
    LOADING: "Loading",
    WEIGHED: "Weighed",
  };
  return map[raw] || raw.replace(/_/g, " ");
}

export default function SalesContractFulfillmentPanel({ contractId, contractNumber, compact = false }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contractId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .get(`/sales-contracts/${contractId}/fulfillment`)
      .then((r) => setData(r.data))
      .catch((err) => setError(extractError(err)))
      .finally(() => setLoading(false));
  }, [contractId]);

  if (!contractId) return null;

  const summary = data?.summary;
  const loadings = data?.loadings || [];
  const code = contractNumber || data?.contract_number || contractId;

  return (
    <section
      className={cn(
        "min-w-0 border-t border-border/80 space-y-3",
        compact ? "mt-4 pt-4" : "mt-6 pt-5 space-y-4",
      )}
      data-testid="sales-contract-fulfillment-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold">Sales fulfillment</h3>
          {!compact && (
            <p className="text-xs text-muted-foreground mt-0.5">
              Outward loadings linked to this contract — dispatched vs pending balance.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-1.5 shrink-0">
          <BackendExportButton
            path={`/sales-contracts/${contractId}/fulfillment/export/excel`}
            filename={`sales-fulfillment-${code}.csv`}
            label="Excel"
            testId="sales-fulfillment-export-excel"
          />
          <BackendExportButton
            path={`/sales-contracts/${contractId}/fulfillment/export/pdf`}
            filename={`sales-fulfillment-${code}.pdf`}
            label="PDF"
            testId="sales-fulfillment-export-pdf"
          />
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading fulfillment…</p>}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {!loading && summary && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-1.5 text-xs">
          <SummaryCell label="Ordered" value={`${fmtQtl(summary.quantity_qtl)} qtl`} />
          <SummaryCell label="Dispatched" value={`${fmtQtl(summary.dispatched_qtl)} qtl`} />
          <SummaryCell label="Pending" value={`${fmtQtl(summary.pending_qtl)} qtl`} highlight />
          <SummaryCell label="Progress" value={`${fmtQtl(summary.progress_pct)}%`} />
          <SummaryCell label="Status" value={summary.order_status || "—"} />
        </div>
      )}

      {!loading && data && loadings.length === 0 && (
        <p className="text-sm text-muted-foreground">No loadings linked to this contract yet.</p>
      )}

      {!loading && loadings.length > 0 && (
        <div className="min-w-0 rounded-md border border-border/70 overflow-x-auto overscroll-x-contain">
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr className="border-b bg-muted/40 text-muted-foreground text-left">
                <th className="py-1.5 px-2 font-medium whitespace-nowrap">Dispatch</th>
                <th className="py-1.5 px-2 font-medium whitespace-nowrap">Date</th>
                <th className="py-1.5 px-2 font-medium whitespace-nowrap">Vehicle</th>
                <th className="py-1.5 px-2 font-medium whitespace-nowrap">State</th>
                <th className="py-1.5 px-2 font-medium text-right whitespace-nowrap">Bags</th>
                <th className="py-1.5 px-2 font-medium text-right whitespace-nowrap">Billable</th>
                <th className="py-1.5 px-2 font-medium text-right whitespace-nowrap">Full</th>
                <th className="py-1.5 px-2 font-medium text-right whitespace-nowrap">Recv.</th>
                <th className="py-1.5 px-2 font-medium whitespace-nowrap">Voucher</th>
              </tr>
            </thead>
            <tbody>
              {loadings.map((row) => (
                <tr key={`${row.dispatch_id}-${row.allocation_id || "x"}`} className="border-b border-border/50 align-middle">
                  <td className="py-1.5 px-2 font-mono whitespace-nowrap">{row.dispatch_code}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">
                    {formatListDate(row.loaded_at || row.gross_weighed_at)}
                  </td>
                  <td className="py-1.5 px-2 whitespace-nowrap">{row.vehicle_number || "—"}</td>
                  <td className="py-1.5 px-2 whitespace-nowrap">
                    <Pill tone={stateTone(row.state)}>{shortState(row.state)}</Pill>
                  </td>
                  <td className="py-1.5 px-2 text-right tabular-nums">{row.bags ?? "—"}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums font-medium">{fmtQtl(row.billable_qtl)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums whitespace-nowrap">{fmtMoney(row.full_amount)}</td>
                  <td className="py-1.5 px-2 text-right tabular-nums whitespace-nowrap">{fmtMoney(row.buyer_receivable)}</td>
                  <td className="py-1.5 px-2 font-mono whitespace-nowrap">{row.voucher_code || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function SummaryCell({ label, value, highlight }) {
  return (
    <div className={`rounded-md border px-2 py-1.5 ${highlight ? "border-bansal-sage/40 bg-bansal-sage/5" : "border-border/60"}`}>
      <p className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-medium tabular-nums truncate">{value}</p>
    </div>
  );
}
